import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Link, Collapse } from '@mui/material';
import { OUTCOME, formatGoal } from '../../../services/sprintHealthService';
import { canManageSurveys, isAuthenticated } from '../../../services/authService';
import { startSurveyRound, closeSurveyRound, reopenSurveyRound } from '../../../services/surveyService';
import SurveyHistoryDialog from './SurveyHistoryDialog';
import { SurveyScores, RetroHeldToggle, RetroBoard, RetroActionsPanel } from './RetroPanels';
import SprintWorkItemsTable from '../../dashboard/SprintWorkItemsTable';
import { get } from '../../../services/apiService';
import {
  A, APill, ABtn, Seg, Vis, Panel, Sec, SecHead, Cap, Bars, BigNum, Stat2, RagChip,
} from './reviewUi';
import {
  HABITS, recent, average, currentPulse, pulseTone, practiceCount,
  velocityRag, reliabilityRag, cycleRag, bugsRag, automatedRag, practiceRag, pulseRag, goalRag, commitRag, effortRag,
} from './rag';

const ADO_BASE = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit';
const DONE_STATES = ['Done', 'Closed', 'Resolved', 'Completed'];
const ACTIVE_STATES = ['Active', 'In Progress', 'Committed', 'Approved'];
const stateTone = (st) => (DONE_STATES.includes(st) ? 'ok' : ACTIVE_STATES.includes(st) ? 'warn' : 'none');
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const dayMonth = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');

const HeadRight = ({ chip, team }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>{chip}<Vis lead={!team} /></Box>
);

/** Anchor target for Summary tile clicks; `flash` outlines it briefly. */
const Card = ({ id, flash, children, sx }) => (
  <Panel id={`sr-${id}`} sx={{ scrollMarginTop: 72, transition: 'box-shadow .2s', boxShadow: flash ? `0 0 0 2px ${A.btn}` : 'none', ...sx }}>{children}</Panel>
);
const MetricCard = ({ id, flash, title, sub, rag, team, children, sx }) => (
  <Card id={id} flash={flash} sx={sx}>
    <Sec>
      <SecHead title={title} sub={sub} right={<HeadRight chip={<RagChip rag={rag} />} team={team} />} />
      {children}
    </Sec>
  </Card>
);

// ── Existing sections, now with a status chip ────────────────────────────────

export const CommitmentsSec = ({ rows, quarterLabel, chip }) => (
  <Sec>
    <SecHead title={`${quarterLabel} commitments`} sub="Completed story points, before and after this sprint" right={<HeadRight chip={chip} />} />
    {rows.length === 0 ? (
      <Cap sx={{ fontSize: 13 }}>No commitments yet. Add this team's Epics or Features in the Quarterly Commitment tab.</Cap>
    ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 640, '& th': { fontWeight: 500, color: A.ink3, fontSize: 12.5, textAlign: 'left', px: 1.5, pb: 1 }, '& td': { px: 1.5, py: 1.75, borderTop: `1px solid ${A.line}`, verticalAlign: 'middle', fontSize: 14, color: A.ink }, '& th:first-of-type, & td:first-of-type': { pl: 0 }, '& th:last-of-type, & td:last-of-type': { pr: 0 } }}>
          <thead><tr><th>Commitment</th><th>Status</th><th>Target</th><Box component="th" sx={{ textAlign: 'right !important' }}>Total SP</Box><th>Progress</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const before = r.beforePct ?? 0;
              const after = r.afterPct ?? r.completionPct ?? 0;
              const gain = Math.max(0, after - before);
              return (
                <tr key={r.adoId}>
                  <td>
                    <Link href={`${ADO_BASE}/${r.adoId}`} target="_blank" rel="noopener" sx={{ color: A.ink2, textUnderlineOffset: 3, textDecorationColor: A.line }}>{r.adoId}</Link>
                    <Box sx={{ mt: 0.25 }}><Box component="span" sx={{ fontSize: 12, color: A.ink3, mr: 0.75 }}>{r.workItemType === 'Product Backlog Item' ? 'PBI' : r.workItemType}</Box>{r.title}</Box>
                  </td>
                  <td>{r.state ? <APill tone={stateTone(r.state)}>{r.state}</APill> : '—'}</td>
                  <Box component="td" sx={{ color: `${A.ink2} !important`, whiteSpace: 'nowrap' }}>{shortDate(r.targetDate)}</Box>
                  <Box component="td" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.totalStoryPoints ?? 0}</Box>
                  <td>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 180 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span>{gain ? <>{before}% → <b>{after}%</b></> : <b>{after}%</b>}</span>
                        {gain ? <Box component="span" sx={{ color: A.good, fontWeight: 600 }}>+{gain} pts</Box> : <Box component="span" sx={{ color: A.ink3 }}>No movement</Box>}
                      </Box>
                      <Box role="img" aria-label={`${after} percent complete`} sx={{ position: 'relative', height: 8, borderRadius: 99, bgcolor: A.line, overflow: 'hidden', minWidth: 140 }}>
                        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${before}%`, bgcolor: A.sage, opacity: 0.55 }} />
                        {gain > 0 && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${before}%`, width: `${gain}%`, bgcolor: A.sageInk }} />}
                      </Box>
                    </Box>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    )}
  </Sec>
);


export const GoalSec = ({ c, hist, editable, onOutcome, onSync, busy, chip }) => {
  const { area, items } = formatGoal(c.goal.text);
  const current = ['met', 'partly', 'not_met'].includes(c.goal.outcome) ? c.goal.outcome : null;
  return (
    <Sec>
      <SecHead title="Sprint goal" sub={c.goal.fetchedAt ? `Synced from ADO on ${dayMonth(c.goal.fetchedAt)}` : 'From ADO Sprint Goal'} right={<HeadRight chip={chip} />} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          {!c.goal.text && <Typography sx={{ color: A.ink2 }}>No sprint goal recorded in ADO for this sprint.</Typography>}
          {area && <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '6px', bgcolor: A.soft, fontSize: 12, fontWeight: 600, color: A.ink2, mb: 0.75 }}>{area}</Box>}
          {items.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, p: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
              {items.map((it, k) => (
                <Box component="li" key={k} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', color: A.ink, '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', bgcolor: A.sage, mt: 1, flex: 'none' } }}>{it}</Box>
              ))}
            </Box>
          )}
          {c.goal.fetchError && <Alert severity="warning" sx={{ mt: 1.5 }}>Couldn't read the goal from ADO: {c.goal.fetchError}</Alert>}
        </Box>
        <Box sx={{ flex: 'none' }}>
          <Cap sx={{ fontSize: 12, mb: 0.75 }}>Goal outcome</Cap>
          <Seg
            value={current}
            ariaLabel="Goal outcome"
            disabled={!editable}
            options={[['met', 'Met'], ['partly', 'Partly'], ['not_met', 'Not met']]}
            onChange={(k) => onOutcome(k === current ? null : k)}
          />
          {editable && (
            <Box sx={{ mt: 1 }}>
              <ABtn variant="ghost" sx={{ height: 30, px: 0.5 }} disabled={busy === 'goal'} onClick={onSync}>{busy === 'goal' ? 'Syncing…' : 'Sync goal from ADO'}</ABtn>
            </Box>
          )}
        </Box>
      </Box>
      <GoalHistory hist={hist} />
    </Sec>
  );
};


export const EffortSec = ({ c, chip, open }) => {
  const a = c.allocation;
  const unit = a.basis === 'sp' ? 'SP' : 'items';
  const [showUnlinked, setShowUnlinked] = useState(!!open);
  useEffect(() => { if (open) setShowUnlinked(true); }, [open]);
  const parts = [
    ['goal', A.sageInk, 'Commitments named in the goal'],
    ['quarterly', A.sage, a.goal ? `Other ${c.quarter} commitments` : `${c.quarter} commitments`],
    ['bau', A.bau, 'Bugs and BAU'],
    ['unlinked', A.warn, 'Not linked to the plan'],
  ].filter(([k]) => k !== 'goal' || a.goal > 0);
  return (
    <Sec>
      <SecHead title="Where the effort went" sub={`Completed work by place in the ADO hierarchy · ${a.total} ${unit}`} right={<HeadRight chip={chip} />} />
      {a.total === 0 ? (
        <Cap sx={{ fontSize: 13 }}>No completed items in this sprint yet.</Cap>
      ) : (
        <>
          <Box role="img" aria-label="Effort allocation" sx={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden', bgcolor: A.line, mt: 0.5, mb: 1.5 }}>
            {parts.map(([k, color]) => a[k] > 0 && <Box key={k} sx={{ width: `${(a[k] / a.total) * 100}%`, bgcolor: color }} />)}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', color: A.ink2, fontSize: 14 }}>
            {parts.map(([k, color, label]) => (
              <Box key={k} component="span" sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                <Box component="i" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color }} />{label} <b style={{ color: A.ink }}>{a[k]} {unit}</b>
              </Box>
            ))}
          </Box>
        </>
      )}
      {c.unlinkedItems?.length > 0 && (
        <Box sx={{ mt: 1.75 }}>
          <Link component="button" sx={{ fontSize: 13, fontWeight: 600, color: A.sageInk }} onClick={() => setShowUnlinked((v) => !v)}>
            {showUnlinked ? 'Hide' : 'Show'} items not linked to the plan ({c.unlinkedItems.length})
          </Link>
          <Collapse in={showUnlinked}>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1, display: 'grid', gap: 0.75 }}>
              {c.unlinkedItems.map((it) => (
                <Box component="li" key={it.id} sx={{ display: 'flex', gap: 1.25, fontSize: 13, flexWrap: 'wrap', color: A.ink }}>
                  <Box component="span" sx={{ color: A.ink3, minWidth: 60 }}>{it.id}</Box><span>{it.title}</span>
                  <Box component="span" sx={{ color: A.ink3 }}>{it.type}{it.sp ? ` · ${it.sp} SP` : ''}</Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Sec>
  );
};



const GoalHistory = ({ hist }) => {
  const h = recent(hist, 5);
  const color = (o) => (o === 'met' ? A.good : o === 'not_met' ? A.bad : o === 'partly' ? '#d99a3a' : A.line);
  return (
    <Box sx={{ mt: 3 }}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: A.ink }}>Goal history</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `200px repeat(${h.length}, 40px)`, gap: 1, alignItems: 'center', mt: 1.5, fontSize: 13, color: A.ink }}>
          <span />
          {h.map((m) => <Box key={m.sprintName} sx={{ fontSize: 11, color: A.ink3, textAlign: 'center' }}>{m.shortName}</Box>)}
          <span>Sprint goal</span>
          {h.map((m) => <Box key={m.sprintName} title={`${m.shortName}: ${OUTCOME[m.goal.outcome]?.label || 'not recorded'}`} sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: color(m.goal.outcome), m: 'auto' }} />)}
          <span>Quarterly delivery moved</span>
          {h.map((m) => <Box key={m.sprintName} title={`${m.shortName}: ${m.delivery ? `${m.delivery.moved} of ${m.delivery.total} moved` : 'not recorded'}`} sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: m.delivery?.moved > 0 ? A.good : A.line, m: 'auto' }} />)}
        </Box>
      </Box>
      <Cap sx={{ mt: 1, fontSize: 12 }}>Newest sprint first · Green: met or moved · Amber: partly met · Red: not met · Grey: not recorded</Cap>
    </Box>
  );
};

// ── Retro survey ─────────────────────────────────────────────────────────────

export const surveyUrl = (team) => `${window.location.origin}/survey/${encodeURIComponent(team)}`;

/**
 * Survey admins (admins, or the User Management toggle) start, copy the link for, stop and reopen
 * the team's anonymous survey; everyone sees its status, and signed-in users can open every past survey.
 */
export const SurveyControls = ({ survey, team, sprint, onChanged, inline = false }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const admin = canManageSurveys();
  const open = survey?.status === 'open';
  const act = async (fn, done) => {
    setBusy(true);
    setMsg('');
    try { await fn(); setMsg(done); await onChanged(); } catch (err) { setMsg(err.message); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(surveyUrl(team)); setMsg('Survey link copied'); } catch { setMsg(`Copy this link: ${surveyUrl(team)}`); }
  };
  const count = survey?.summary?.responseCount ?? 0;
  const status = !survey
    ? 'Survey not started. Start it, then email the link to the team.'
    : `Survey ${open ? 'open' : 'stopped'} · ${count} ${count === 1 ? 'response' : 'responses'}.`;
  return (
    <Box sx={inline ? { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' } : undefined}>
      <Cap sx={{ fontSize: 13 }}>{status}</Cap>
      {survey && open && <Box sx={{ mt: inline ? 0 : 1, p: 1, bgcolor: A.soft, borderRadius: '8px', fontSize: 12, wordBreak: 'break-all', color: A.ink2 }}>{surveyUrl(team)}</Box>}
      {admin && (
        <Box sx={{ mt: inline ? 0 : 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!survey && <ABtn variant="primary" disabled={busy} onClick={() => act(() => startSurveyRound(team, sprint), 'Survey started. Copy the link and send it to the team.')}>Start survey</ABtn>}
          {open && <><ABtn onClick={copy}>Copy survey link</ABtn><ABtn disabled={busy} onClick={() => act(() => closeSurveyRound(survey.roundId), 'Survey stopped')}>Stop survey</ABtn></>}
          {survey && !open && <ABtn disabled={busy} onClick={() => act(() => reopenSurveyRound(survey.roundId), 'Survey reopened')}>Reopen survey</ABtn>}
        </Box>
      )}
      {msg && <Cap sx={{ mt: 1, wordBreak: 'break-all' }}><span role="status">{msg}</span></Cap>}
      {isAuthenticated() && (
        <>
          <Link component="button" type="button" onClick={() => setHistoryOpen(true)} sx={{ mt: inline ? 0 : 1, fontSize: 13, display: 'inline-block' }}>All surveys</Link>
          <SurveyHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} team={team} onChanged={onChanged} />
        </>
      )}
    </Box>
  );
};

/** Last sprints' pulse as bars, newest first, coloured by the pulse rule. */
export const PulseTrend = ({ data, hist }) => {
  const h = recent(hist, 5);
  const vals = h.map((m, k) => (k === 0 ? currentPulse(data) : m.pulse?.avg ?? null));
  const col = { r: A.bad, a: '#e08a00', g: A.good, n: A.line };
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mt: 0.5 }}>
      {h.map((m, k) => (
        <Box key={m.sprintName} title={`${m.shortName}: ${vals[k] != null ? vals[k].toFixed(1) : 'no score'}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, fontSize: 11, color: A.ink3 }}>
          <span style={{ color: A.ink }}>{vals[k] != null ? vals[k].toFixed(1) : '—'}</span>
          <Box sx={{ width: 30, height: vals[k] != null ? Math.round((vals[k] / 5) * 40) : 6, bgcolor: col[pulseTone(vals[k])], borderRadius: '3px 3px 0 0' }} />
          {m.shortName}
        </Box>
      ))}
    </Box>
  );
};

export const SprintItemsPanel = ({ sprint, team, endDate }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (items || loading) return;
    setLoading(true);
    try {
      const res = await get(`/api/sprint-report/work-items/${encodeURIComponent(sprint)}/${encodeURIComponent(team)}`, null, 'json', { useCache: false });
      const d = res?.data;
      setItems(Array.isArray(d?.workItems) ? d.workItems : Array.isArray(d) ? d : []);
    } catch (err) {
      console.error('Error loading sprint work items:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  const sprintEnded = endDate ? Date.now() > new Date(endDate).getTime() : false;
  return (
    <SprintWorkItemsTable
      workItems={items || []}
      sprintEnded={sprintEnded}
      collapsible
      defaultExpanded={false}
      onFirstExpand={load}
      loading={loading || items === null}
      sx={{ borderRadius: '14px', border: `1px solid ${A.line}`, boxShadow: 'none' }}
    />
  );
};


// ── Practice dashes ──────────────────────────────────────────────────────────

/** One dash per recent sprint, newest first: green in place, red missed, grey not recorded. */
export const Dashes = ({ values, labels }) => (
  <Box sx={{ display: 'flex', gap: '3px' }}>
    {values.map((v, k) => (
      <Box key={k} title={`${labels[k]}: ${v === true ? 'in place' : v === false ? 'missed' : 'not recorded'}`} sx={{ width: 20, height: 6, borderRadius: 3, bgcolor: v === true ? A.good : v === false ? A.bad : A.line }} />
    ))}
  </Box>
);

// ── New metric cards ─────────────────────────────────────────────────────────

const VelocityCard = ({ c, hist, flash }) => {
  const avg = Math.round(average(hist.map((m) => m.velocity)) || 0);
  return (
    <MetricCard id="velocity" flash={flash} title="Velocity" sub="Your own planning trend. Not compared across teams." rag={velocityRag(c, hist)} team>
      <BigNum value={c.velocity} unit="SP" />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
        <Box sx={{ flex: 1 }}><Bars items={recent(hist).map((m) => ({ label: m.shortName, value: m.velocity, current: m.sprintName === c.sprintName }))} suffix=" SP" /></Box>
        <Cap sx={{ pb: 3, whiteSpace: 'nowrap' }}>avg {avg} SP</Cap>
      </Box>
    </MetricCard>
  );
};

const ReliabilityCard = ({ c, hist, flash }) => (
  <MetricCard id="reliability" flash={flash} title="Forecast reliability" sub={`${c.forecast.doneOfCommitted} of ${c.forecast.committed} items planned at sprint start were done`} rag={reliabilityRag(c)}>
    <BigNum value={c.forecast.reliability} unit="%" />
    <Bars items={recent(hist).map((m) => ({ label: m.shortName, value: m.forecast.reliability, current: m.sprintName === c.sprintName }))} max={100} suffix="%" />
    <Cap sx={{ mt: 1.25 }}><b style={{ color: A.ink }}>{c.carryOver}</b> not done and carried over 2+ sprints</Cap>
  </MetricCard>
);

const CycleCard = ({ c, flash }) => (
  <MetricCard id="cycle" flash={flash} title="Cycle time" sub={c.cycle.basis === 'activated' ? 'Active to Done, from ADO dates' : 'Sprint entry to Done (approximate)'} rag={cycleRag(c)}>
    <Stat2 items={[[c.cycle.median != null ? `${c.cycle.median} days` : '—', 'Median'], [c.cycle.p85 != null ? `${c.cycle.p85} days` : '—', '85th percentile']]} />
  </MetricCard>
);

const qualityOf = (c) => c.quality || { bugsRaised: c.escapedDefects, bugsProduction: c.escapedDefects, bugsNonProduction: 0, testsCreated: 0, testsAutomated: 0, testsManual: 0, automationPct: null };

const BugsCard = ({ c, flash }) => {
  const q = qualityOf(c);
  return (
    <MetricCard id="bugs" flash={flash} title="Bugs raised" sub="Bugs raised in the sprint under the team's ADO areas." rag={bugsRag(q)}>
      <Stat2 items={[[q.bugsRaised, 'Bugs raised'], [`${q.bugsProduction} prod · ${q.bugsNonProduction} non-prod`, 'By environment']]} />
    </MetricCard>
  );
};

const TestsCard = ({ c, flash }) => {
  const q = qualityOf(c);
  return (
    <MetricCard id="tests" flash={flash} title="Tests created" sub="Counted if created in the sprint under the team's ADO areas, or linked to its items." rag={{ tone: 'n', reason: '' }}>
      <BigNum value={q.testsCreated} unit="tests" />
    </MetricCard>
  );
};

const AutomatedCard = ({ c, flash }) => {
  const q = qualityOf(c);
  return (
    <MetricCard id="automated" flash={flash} title="Tests automated" sub="Share of this sprint's tests that are automated. Green above 75%, amber 50 to 75%, red below 50%." rag={automatedRag(q)}>
      <Stat2 items={[[q.testsAutomated, 'Automated'], [q.testsManual, 'Manual'], [q.automationPct != null ? `${q.automationPct}%` : '—', 'Automated share']]} />
    </MetricCard>
  );
};

export const PracticeCard = ({ c, hist, flash }) => {
  const h = recent(hist, 5);
  const labels = h.map((m) => m.shortName);
  return (
    <MetricCard id="practice" flash={flash} title="Practice" sub={`${practiceCount(c.practice)} of ${HABITS.length} habits this sprint. Newest sprint first; green in place, red missed, grey not recorded.`} rag={practiceRag(c)} team>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 320, borderCollapse: 'collapse', '& th': { fontWeight: 500, color: A.ink3, fontSize: 12, textAlign: 'center', pb: 0.75 }, '& th:first-of-type': { textAlign: 'left' }, '& td': { py: 0.75, borderTop: `1px solid ${A.line}`, fontSize: 13.5, color: A.ink }, '& td:not(:first-of-type)': { textAlign: 'center' } }}>
          <thead><tr><th>Habit</th>{labels.map((l) => <th key={l}>{l}</th>)}</tr></thead>
          <tbody>
            {HABITS.map(([k, label]) => (
              <tr key={k}>
                <td>{label}</td>
                {h.map((m) => <td key={m.sprintName}><Dashes values={[m.practice?.[k] ?? null]} labels={[m.shortName]} /></td>)}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </MetricCard>
  );
};

/**
 * Sprint retro: one strip with the team pulse, trend, Retro held and the survey controls, then each
 * survey question in two columns (how many answered, average, bar, the 1 to 5 scale).
 */
export const SprintRetroCard = ({ c, hist, data, team, sprint, editable, onChanged, flash }) => {
  const score = currentPulse(data);
  return (
    <MetricCard id="pulse" flash={flash} title="Sprint retro" sub="Anonymous survey: each question's average and the 1 to 5 scale. Hover a score to see how many picked it." rag={pulseRag(score)} sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px', alignItems: 'flex-end', pb: 2, mb: 2, borderBottom: `1px solid ${A.line}` }}>
        <Box>
          <Cap>Team pulse</Cap>
          <Box sx={{ fontSize: 34, fontWeight: 300, lineHeight: 1.15, color: A.ink }}>{score != null ? score.toFixed(1) : '—'}<Box component="span" sx={{ fontSize: 15, color: A.ink3 }}> / 5</Box></Box>
        </Box>
        <Box><Cap>Last 5 sprints</Cap><PulseTrend data={data} hist={hist} /></Box>
        <Box sx={{ minWidth: 180 }}><RetroHeldToggle c={c} team={team} sprint={sprint} editable={editable} onChanged={onChanged} /></Box>
        <Box sx={{ ml: { lg: 'auto' } }}><SurveyControls survey={data.survey} team={team} sprint={sprint} onChanged={onChanged} inline /></Box>
      </Box>
      <SurveyScores c={c} survey={data.survey} cards columns={2} />
    </MetricCard>
  );
};

/**
 * Team health, layout B: Sprint retro (two thirds) beside a column with Practice and the retro
 * actions, then Start / Stop / Continue across the full width. Used on both Summary and Details.
 */
export const TeamHealthSection = ({ c, hist, data, team, sprint, editable, loggedIn, onChanged, focus }) => {
  const f = (id) => focus === id;
  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,2fr) minmax(0,1fr)' }, gap: 2.5, alignItems: 'stretch' }}>
        <SprintRetroCard c={c} hist={hist} data={data} team={team} sprint={sprint} editable={editable} onChanged={onChanged} flash={f('pulse')} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
          <PracticeCard c={c} hist={hist} flash={f('practice')} />
          <RetroActionsPanel c={c} team={team} sprint={sprint} editable={editable} onChanged={onChanged} />
        </Box>
      </Box>
      <RetroBoard c={c} data={data} team={team} sprint={sprint} loggedIn={loggedIn} editable={editable} onChanged={onChanged} flash={f('retro')} withActions={false} />
    </Box>
  );
};

// ── Details tab ──────────────────────────────────────────────────────────────

const Group = ({ title, children, cols = '1fr' }) => (
  <Box sx={{ mt: 3 }}>
    <Typography component="h2" sx={{ fontSize: 18, fontWeight: 500, color: A.sageInk, mb: 1.5 }}>{title}</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 2, alignItems: 'start' }}>{children}</Box>
  </Box>
);

export const JUMPS = [
  ['goal', 'Sprint goal'], ['commit', 'Commitments'], ['effort', 'Effort'], ['items', 'Work items'],
  ['reliability', 'Reliability'], ['velocity', 'Velocity'], ['cycle', 'Cycle time'],
  ['bugs', 'Bugs'], ['tests', 'Tests'], ['automated', 'Automated'],
  ['pulse', 'Sprint retro'], ['practice', 'Practice'], ['retro', 'Start, stop, continue'],
];

const DetailsTab = ({
  c, hist, data, delivery, team, sprint, editable, loggedIn, busy, focus, effortOpen,
  onOutcome, onSync, onChanged, onJump,
}) => {
  const f = (id) => focus === id;
  const two = { xs: '1fr', md: '1fr 1fr' };
  const three = { xs: '1fr', md: 'repeat(3, 1fr)' };
  return (
    <Box>
      <Box role="navigation" aria-label="Jump to a section" sx={{ position: 'sticky', top: 0, zIndex: 5, bgcolor: '#F8F9FA', py: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {JUMPS.map(([id, label]) => <ABtn key={id} sx={{ height: 30, px: 1.25, fontSize: 13 }} onClick={() => onJump(id)}>{label}</ABtn>)}
      </Box>

      <Group title="Delivery">
        <Card id="goal" flash={f('goal')}>
          <GoalSec c={c} hist={hist} editable={editable} busy={busy} onOutcome={onOutcome} onSync={onSync} chip={<RagChip rag={goalRag(c)} />} />
        </Card>
        <Card id="commit" flash={f('commit')}>
          <CommitmentsSec rows={data.quarterCommitments || []} quarterLabel={`${c.quarter} ${c.year}`} chip={<RagChip rag={commitRag(delivery)} />} />
        </Card>
        <Card id="effort" flash={f('effort')}><EffortSec c={c} open={effortOpen} chip={<RagChip rag={effortRag(c)} />} /></Card>
        <Box id="sr-items" sx={{ scrollMarginTop: 72 }}><SprintItemsPanel key={`${sprint}|${team}`} sprint={sprint} team={team} endDate={c.endDate} /></Box>
      </Group>

      <Group title="Flow" cols={two}>
        <ReliabilityCard c={c} hist={hist} flash={f('reliability')} />
        <VelocityCard c={c} hist={hist} flash={f('velocity')} />
        <CycleCard c={c} flash={f('cycle')} />
      </Group>

      <Group title="Quality" cols={three}>
        <BugsCard c={c} flash={f('bugs')} />
        <TestsCard c={c} flash={f('tests')} />
        <AutomatedCard c={c} flash={f('automated')} />
      </Group>

      <Group title="Team health">
        <TeamHealthSection c={c} hist={hist} data={data} team={team} sprint={sprint} editable={editable} loggedIn={loggedIn} onChanged={onChanged} focus={focus} />
      </Group>
    </Box>
  );
};

export { qualityOf };
export default DetailsTab;
